/**
 * ActivityPage - running feed of the most recently touched tickets
 * (Greg, 2026-07-30). Agent-only; the customer portal has no
 * equivalent, and the route guard in App.tsx sends customers back to
 * their own list.
 *
 * One row per TICKET, not per event: a ticket that took four replies
 * this morning is one thing that happened, not four. "Touched" means a
 * comment landed or the ticket was created - property edits are not
 * activity for this purpose (see the backend endpoint's docstring).
 *
 * Table shape is Greg's: ID, Ticket Title, Owner (client), Status,
 * Last-Touched By, Time. It reuses the inbox's table classes so the
 * two surfaces read as the same product, including the mobile
 * card-per-row treatment from styles/index.css.
 *
 * Since 2026-08-08 (plan A8) the server interleaves completed TASKS
 * into the same date-sorted feed, discriminated by ``kind``; those
 * render via ActivityTaskTableRow below. Ticket rows are unchanged.
 */
import { useNavigate } from "react-router-dom";

import { Spinner } from "@/components/ui";
import { StatusBadge } from "@/components/tickets/StatusBadge";
import { useActivityFeed } from "@/hooks/useActivityFeed";
import { usePageTitle } from "@/hooks/usePageTitle";
import type { ActivityTaskRow, ActivityTicketRow } from "@/types/api";

/** Same wording as the inbox's Last Activity column so a ticket reads
 * identically on both surfaces. */
function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = Math.max(0, Date.now() - then);
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
  return new Date(iso).toLocaleDateString();
}

/** Small tint on the "Last touched by" name so an agent can tell at a
 * glance whether a human or the AI moved the ticket. */
function touchedByClass(kind: string): string {
  switch (kind) {
    case "api_key":
      return "text-violet-700";
    case "agent":
      return "text-blue-700";
    case "system":
      return "text-neutral-400";
    default:
      return "text-neutral-700";
  }
}

function ActivityTableRow({ row }: { row: ActivityTicketRow }): JSX.Element {
  const navigate = useNavigate();
  return (
    <tr
      onClick={() => navigate(`/tickets/${row.public_code.toLowerCase()}`)}
      className="cursor-pointer transition-colors hover:bg-neutral-50"
      data-testid="activity-row"
      data-public-code={row.public_code}
    >
      {/* ``data-label`` + the ``tickets-card-*`` classes drive the
          mobile card layout in styles/index.css, the same way the
          inbox rows do. On desktop they are inert. */}
      <td
        className="tickets-card-code whitespace-nowrap px-3 py-3 font-mono text-xs font-medium text-neutral-900"
        data-label="ID"
      >
        {row.public_code}
      </td>
      {/* ``data-code`` lets the card header render as "{code} -
          {subject}" via a ::before, so hiding the code cell on phones
          does not lose the identifier. */}
      <td
        className="tickets-card-subject px-3 py-3 text-sm text-neutral-800"
        data-label="Ticket"
        data-code={row.public_code}
      >
        {row.subject}
      </td>
      {/* Owner, Status and Last-touched-by are atomic values, so they
          hold one line and the Ticket column absorbs the slack - it is
          the only cell where wrapping reads as intended. */}
      <td
        className="whitespace-nowrap px-3 py-3 text-sm text-neutral-700"
        data-label="Owner"
      >
        {row.owner_name || row.owner_email}
      </td>
      <td className="whitespace-nowrap px-3 py-3" data-label="Status">
        <StatusBadge status={row.status} />
      </td>
      <td
        className={`whitespace-nowrap px-3 py-3 text-sm ${touchedByClass(
          row.last_touched_kind,
        )}`}
        data-label="Last touched by"
        data-kind={row.last_touched_kind}
      >
        {row.last_touched_by}
      </td>
      {/* One line, always: "13 hours ago" broke after "13" whenever a
          long subject squeezed the column (Greg 2026-07-31). */}
      <td
        className="whitespace-nowrap px-3 py-3 text-xs text-neutral-500"
        data-label="Time"
      >
        {formatRelative(row.last_activity_at)}
      </td>
    </tr>
  );
}

/**
 * A completed task interleaved into the feed (plan 2026-08-08 A8).
 * Same six-column grid as the ticket rows so the table stays one
 * table: ID carries a "Task" marker instead of a code, Ticket carries
 * the task title, Owner names the agent whose list it was, Status
 * says "Task completed", and the last column before Time shows the
 * linked ticket's code when there is one. Rows with a linked ticket
 * navigate to it; rows without are inert - there is nowhere to go.
 */
function ActivityTaskTableRow({ row }: { row: ActivityTaskRow }): JSX.Element {
  const navigate = useNavigate();
  const code = row.task_ticket_public_code;
  return (
    <tr
      onClick={
        code
          ? () => navigate(`/tickets/${code.toLowerCase()}`)
          : undefined
      }
      className={
        code
          ? "cursor-pointer transition-colors hover:bg-neutral-50"
          : undefined
      }
      data-testid="activity-task-row"
      data-task-id={row.task_id}
    >
      <td
        className="tickets-card-code whitespace-nowrap px-3 py-3 text-xs font-medium text-neutral-500"
        data-label="ID"
      >
        <i
          className="fa-duotone fa-thin fa-list-check mr-1 text-primary-600"
          aria-hidden="true"
        />
        Task
      </td>
      <td
        className="tickets-card-subject px-3 py-3 text-sm text-neutral-800"
        data-label="Ticket"
        data-code="Task"
      >
        {row.task_title}
      </td>
      <td
        className="whitespace-nowrap px-3 py-3 text-sm text-neutral-700"
        data-label="Owner"
      >
        {row.task_owner_name || row.task_owner_email}
      </td>
      <td className="whitespace-nowrap px-3 py-3" data-label="Status">
        <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-medium text-neutral-600">
          <i className="fa-duotone fa-thin fa-check" aria-hidden="true" />
          Task completed
        </span>
      </td>
      <td
        className="whitespace-nowrap px-3 py-3 font-mono text-xs text-neutral-500"
        data-label="Last touched by"
      >
        {code ?? ""}
      </td>
      <td
        className="whitespace-nowrap px-3 py-3 text-xs text-neutral-500"
        data-label="Time"
      >
        {formatRelative(row.last_activity_at)}
      </td>
    </tr>
  );
}

export function ActivityPage(): JSX.Element {
  usePageTitle("Activity - Buyist Support");
  const feed = useActivityFeed();
  const rows = feed.data ?? [];

  return (
    <div className="w-full">
      <div className="mb-6 flex items-center gap-3">
        <i
          className="fa-duotone fa-thin fa-wave-pulse text-3xl text-primary-600"
          aria-hidden="true"
        />
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900">Activity</h1>
          <p className="mt-1 text-sm text-neutral-500" data-testid="activity-subtitle">
            {feed.isLoading
              ? "Loading..."
              : `${rows.length} ticket${
                  rows.length === 1 ? "" : "s"
                } touched in the last 7 days, newest first`}
          </p>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white max-lg:overflow-visible max-lg:rounded-none max-lg:border-0 max-lg:bg-transparent">
        {feed.isLoading && (
          <div className="flex items-center justify-center px-4 py-12">
            <Spinner />
          </div>
        )}

        {feed.isError && (
          <div className="px-4 py-8 text-center text-sm text-red-700">
            {feed.error instanceof Error
              ? feed.error.message
              : "Failed to load activity."}
          </div>
        )}

        {!feed.isLoading && !feed.isError && rows.length === 0 && (
          <div className="flex flex-col items-center justify-center px-4 py-12 text-center">
            <i
              className="fa-duotone fa-thin fa-wave-pulse text-4xl text-neutral-300"
              aria-hidden="true"
            />
            <p className="mt-2 text-sm font-medium text-neutral-700">
              No ticket activity in the last 7 days
            </p>
          </div>
        )}

        {!feed.isLoading && !feed.isError && rows.length > 0 && (
          <table className="tickets-list-table min-w-full divide-y divide-neutral-100">
            <thead className="bg-neutral-50">
              <tr className="text-left text-xs font-medium uppercase tracking-wider text-neutral-500">
                <th className="px-3 py-2">ID</th>
                <th className="px-3 py-2">Ticket</th>
                <th className="px-3 py-2">Owner</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Last touched by</th>
                <th className="whitespace-nowrap px-3 py-2">Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {rows.map((row) =>
                row.kind === "task" ? (
                  <ActivityTaskTableRow key={row.task_id} row={row} />
                ) : (
                  <ActivityTableRow key={row.ticket_id} row={row} />
                ),
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
